import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import {
  ConfiguredActionItemDto,
  DefectReportContextDto,
  InspectionEntryFilterDto,
  InspectionEntryItemDto,
  InspectionEntryService,
  SortDto,
  TeamItemDto,
  TeamService,
} from '@artemis-software/wr-api';
import { createForm } from '@sonofabit/ng-core';
import { currentTenant } from '@/utils/admin-utils';
import { firstValueFrom } from 'rxjs';
import { NbDialogService } from '@nebular/theme';
import {
  InspectionEntryRepairTaskPatch,
  InspectionEntryStatisticDialogComponent,
} from '@pages/inspection-entry-statistic-page/inspection-entry-statistic-dialog/inspection-entry-statistic-dialog.component';
import RepairTaskStatusEnum = InspectionEntryItemDto.RepairTaskStatusEnum;

export const WITHOUT_REPAIR_TASK = 'WITHOUT_REPAIR_TASK';
type RepairTaskFilterOption = RepairTaskStatusEnum | typeof WITHOUT_REPAIR_TASK;

@Component({
  standalone: false,
  selector: 'wr-defect-report-page',
  templateUrl: './defect-report-page.component.html',
  styleUrls: ['./defect-report-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DefectReportPageComponent {

  private readonly inspectionEntryService = inject(InspectionEntryService);
  private readonly teamService = inject(TeamService);
  private readonly dialogService = inject(NbDialogService);

  private readonly pageSize = 500;

  readonly currentTenant = currentTenant();
  readonly isExternal = this.currentTenant()?.type === 'External';
  readonly repairTaskOptions: RepairTaskFilterOption[] = [WITHOUT_REPAIR_TASK, ...Object.values(RepairTaskStatusEnum)];
  readonly withoutRepairTask = WITHOUT_REPAIR_TASK;

  readonly filterForm = this.createForm();
  readonly context = signal<DefectReportContextDto | undefined>(undefined);
  readonly selectedInternalTenantId = signal<string | undefined>(undefined);
  readonly selectedInternalTenantName = computed(() => {
    const id = this.selectedInternalTenantId();
    return this.context()?.internalTenants.find(tenant => tenant.id === id)?.name;
  });
  readonly selectedBuildingIds = signal<string[]>([]);
  readonly entries = signal<InspectionEntryItemDto[]>([]);
  readonly availableActions = signal<ConfiguredActionItemDto[]>([]);
  readonly initialized = signal(false);
  readonly loading = signal(false);
  readonly loadingMore = signal(false);
  readonly hasMore = signal(true);
  readonly sort = signal<SortDto | undefined>(undefined);

  readonly sortableColumns = [
    { key: 'sectionText', label: 'Adresse' },
    { key: 'inspectionSendingTimestamp', label: 'Datum' },
    { key: 'deadline', label: 'Frist' },
    { key: 'action', label: 'Maßnahme' },
  ] as const;

  private page = 0;
  private resetVersion = 0;

  constructor() {
    void this.initialize();
  }

  private createForm() {
    const form = createForm(({ field }) => ({
      internalTenantId: field({ value: undefined as string | undefined }),
      externalTenantId: field({ value: undefined as string | undefined }),
      checkpointId: field({ value: undefined as string | undefined }),
      action: field({ value: undefined as ConfiguredActionItemDto | undefined }),
      team: field({ value: undefined as TeamItemDto | undefined }),
      repairTask: field({ value: [] as RepairTaskFilterOption[] }),
    }));
    form.controls.internalTenantId.valueChanges.subscribe(internalTenantId => {
      this.selectedInternalTenantId.set(internalTenantId ?? undefined);
      form.controls.checkpointId.setValue(undefined, { emitEvent: false });
      form.controls.action.setValue(undefined, { emitEvent: false });
    });
    form.valueChanges.subscribe(() => {
      if (this.initialized()) this.reset();
    });
    return form;
  }

  private async initialize(): Promise<void> {
    const [context, ownTeam] = await Promise.all([
      firstValueFrom(this.inspectionEntryService.findDefectReportContext()),
      this.findOwnTeam(),
    ]);
    this.context.set(context);
    this.filterForm.controls.internalTenantId.setValue(context.defaultInternalTenantId, { emitEvent: false });
    this.selectedInternalTenantId.set(context.defaultInternalTenantId);
    if (ownTeam) this.filterForm.controls.team.setValue(ownTeam, { emitEvent: false });
    this.initialized.set(true);
    this.reset();
  }

  private async findOwnTeam(): Promise<TeamItemDto | undefined> {
    const tenant = this.currentTenant();
    if (tenant?.type !== 'External') return undefined;
    const teams = await firstValueFrom(this.teamService.findAll({
      ownTeamsOnly: true,
      tenantId: tenant.id,
      page: 0,
      size: 1,
      sort: { sort: 'name', order: SortDto.OrderEnum.Asc },
    }));
    return teams[0];
  }

  toggleSort(key: string): void {
    const current = this.sort();
    this.sort.set({
      sort: key,
      order:
        current?.sort === key && current.order === SortDto.OrderEnum.Asc
          ? SortDto.OrderEnum.Desc
          : SortDto.OrderEnum.Asc,
    });
    this.reset();
  }

  onBuildingsChange(ids: string[]): void {
    this.selectedBuildingIds.set(ids);
    if (this.initialized()) this.reset();
  }

  reset(): void {
    this.resetVersion += 1;
    this.page = 0;
    this.entries.set([]);
    this.hasMore.set(true);
    this.loading.set(false);
    this.loadingMore.set(false);
    void this.loadAvailableActions();
    void this.loadMore();
  }

  private buildFilter(): InspectionEntryFilterDto {
    const controls = this.filterForm.controls;
    const buildingIds = this.selectedBuildingIds();
    const repairTask = controls.repairTask.value ?? [];
    const statuses = repairTask.filter((option): option is RepairTaskStatusEnum => option !== WITHOUT_REPAIR_TASK);
    return {
      defectReport: true,
      internalTenantId: controls.internalTenantId.value ?? undefined,
      externalTenantId: controls.externalTenantId.value ?? undefined,
      checkpointId: controls.checkpointId.value ?? undefined,
      actionId: controls.action.value?.id,
      teamId: controls.team.value?.id,
      buildingIds: buildingIds.length > 0 ? buildingIds : undefined,
      repairTaskStatuses: statuses.length > 0 ? statuses : undefined,
      withoutRepairTask: repairTask.includes(WITHOUT_REPAIR_TASK) || undefined,
    };
  }

  private async loadAvailableActions(): Promise<void> {
    const version = this.resetVersion;
    try {
      const actions = await firstValueFrom(
        this.inspectionEntryService.findStatisticActions({ ...this.buildFilter(), actionId: undefined }),
      );
      if (version === this.resetVersion) this.availableActions.set(actions);
    } catch (error) {
      console.error('Loading defect report actions failed:', error);
    }
  }

  async loadMore(): Promise<void> {
    if (!this.initialized() || this.loading() || this.loadingMore() || !this.hasMore()) return;

    const isFirstPage = this.page === 0;
    (isFirstPage ? this.loading : this.loadingMore).set(true);
    const version = this.resetVersion;

    try {
      const batch = await firstValueFrom(this.inspectionEntryService.findAllForStatistics({
        ...this.buildFilter(),
        sort: this.sort(),
        page: this.page,
        size: this.pageSize,
      }));
      if (version !== this.resetVersion) return;
      this.page += 1;
      if (batch.length < this.pageSize) this.hasMore.set(false);
      this.entries.update(current => [...current, ...batch]);
    } finally {
      if (version === this.resetVersion) {
        this.loading.set(false);
        this.loadingMore.set(false);
      }
    }
  }

  openEntry(item: InspectionEntryItemDto): void {
    this.dialogService
      .open(InspectionEntryStatisticDialogComponent, { context: { item, showRepairTask: true } })
      .onClose.subscribe((patch?: InspectionEntryRepairTaskPatch) => {
        if (patch) this.patchEntry(item.id, patch);
      });
  }

  private patchEntry(entryId: string, patch: InspectionEntryRepairTaskPatch): void {
    this.entries.update(entries => entries.map(entry => entry.id === entryId ? { ...entry, ...patch } : entry));
  }
}
